import { useQuery } from '@tanstack/react-query';
import {
  fetchExternalLeads,
  fetchExternalLead,
  type ExternalLeadListResponse,
} from '../services/api/externalLeads';
import type { Lead } from '../services/api/leads';

/**
 * Phase 105 (SC-2): read-only external lead list + detail hooks, backed by
 * GET /api/external/leads and GET /api/external/leads/:id.
 *
 * v4.0 Landmine #8: the underlying transport (services/api/externalLeads.ts)
 * uses fetch() directly with X-Audience: external — it does NOT route
 * through services/api/client.ts.
 *
 * retry: false on both — a 403 from a revoked sales-viewer grant
 * (T-105-REVOKE / T-105-CACHE) must surface immediately as an access-denied
 * state rather than being retried three times. No staleTime is set: the
 * TanStack default of 0 means a revoked grant is discovered on the next
 * mount or window refocus rather than being served from a warm cache.
 *
 * Phase 105, Plan 105-10 / D-13: `ExternalLeadListResponse` (and therefore
 * `data?.leadStalenessDays`) flows through this hook unchanged — no query
 * key or retry behaviour change was needed to expose it to the page.
 */
export function useExternalLeads() {
  return useQuery<ExternalLeadListResponse>({
    queryKey: ['external-leads'],
    queryFn: fetchExternalLeads,
    retry: false,
  });
}

export function useExternalLead(id: number) {
  return useQuery<Lead>({
    queryKey: ['external-lead', id],
    queryFn: () => fetchExternalLead(id),
    enabled: Number.isInteger(id) && id > 0,
    retry: false,
  });
}
